import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { height as heightStyled, border, borderRadius } from 'styled-system';
import Text from '../../atoms/Text';
import FormFieldLabel from '../FormFieldLabel';
import { FormFieldLabelSizePropType } from '../FormFieldLabel/FormFieldLabel';
import FormFieldDescription from '../FormFieldDescription';

const fontSize = (props) => {
  const { size } = props;
  let fs = '';
  switch (size) {
    case 's':
      fs = 's';
      break;
    default:
      fs = 'm';
      break;
  }
  return themeGet(`fontSizes.${fs}`);
};

const padding = (props) => {
  const { size } = props;
  switch (size) {
    case 's':
      return '0 12px';
    case 'l':
      return '0 20px';
    default:
      return '0 16px';
  }
};

const Margin = styled.div`
  margin-left: -2px;
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;
`;

const StyledInput = styled.input`
  display: inline-block;
  position: relative;
  flex: 1 1 auto;
  width: 1%;
  min-width: 0;
  vertical-align: middle;
  padding: ${padding};
  outline: 0;
  -webkit-appearance: none;
  line-height: normal;
  font-size: ${fontSize}px;
  background: ${({ disabled }) =>
    disabled ? themeGet('colors.grayscale.300') : themeGet('colors.white')};
  border: 1px solid
    ${({ disabled }) =>
      disabled
        ? themeGet('colors.grayscale.300')
        : themeGet('colors.grayscale.400')};
  border-color: ${({ error }) => (error ? themeGet('colors.error') : null)};
  border-color: ${({ success }) =>
    success ? themeGet('colors.success') : null};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : null)};
  &:focus {
    border-color: ${themeGet('colors.grayscale.600')};
  }
  ${heightStyled};
  ${border};
  ${borderRadius};
`;

const InputFieldWithButton = ({
  label = null,
  labelSize,
  description,
  id = null,
  name,
  placeholder = '',
  success = null,
  error = null,
  touched = false,
  onChange,
  onBlur,
  value,
  Button,
  borderRadius = 2,
  type = 'text',
  size = 'm',
  ...props
}) => (
  <>
    {!!label && (
      <FormFieldLabel htmlFor={id} size={labelSize}>
        {label}
      </FormFieldLabel>
    )}
    {!!description && (
      <FormFieldDescription>{description}</FormFieldDescription>
    )}
    <InputGroup
      // custom data attribute for AB test FST164 - Booking Page - Design for New Users
      data-testid="email-container"
    >
      <StyledInput
        id={id}
        type={type}
        size={size}
        {...props}
        name={name}
        value={value}
        placeholder={placeholder}
        success={success}
        error={touched && error ? 1 : 0}
        onChange={onChange}
        onBlur={onBlur}
        borderRadius={`${borderRadius ?? 0}px 0 0 ${borderRadius ?? 0}px`}
      />
      {Button && (
        <Margin>
          <Button
            borderRadius={`0 ${borderRadius ?? 0}px ${borderRadius ?? 0}px 0`}
          />
        </Margin>
      )}
    </InputGroup>
    {touched && error && (
      <Text color="error" fontSize="xs" mt="5px" mb="0">
        {error}
      </Text>
    )}
    {success ? (
      <Text color="success" fontSize="xs" mt="5px" mb="0">
        {success}
      </Text>
    ) : null}
  </>
);

InputFieldWithButton.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  labelSize: FormFieldLabelSizePropType,
  description: PropTypes.any,
  size: PropTypes.oneOf(['s', 'm', 'l']),
  error: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.bool,
  ]),
  success: PropTypes.string,
  borderRadius: PropTypes.number,
  touched: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  value: PropTypes.any,
  Button: PropTypes.func,
};

export default InputFieldWithButton;
